/**
 * Web Push subscription helpers (VAPID + Django API).
 */

import { getCsrfToken } from '@/utils/csrf';

const API_BASE = '';

/** Push subscribe works in production builds; in dev, only if a service worker is already active (e.g. preview). */
export async function serviceWorkerReadyForPush(): Promise<boolean> {
  if (import.meta.env.PROD) {
    return true;
  }
  const reg = await navigator.serviceWorker.getRegistration();
  return Boolean(reg?.active);
}

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function fetchVapidPublicKey(): Promise<string | null> {
  const res = await fetch(`${API_BASE}/api/v2/push/vapid-public-key/`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { vapid_public_key?: string };
  const k = typeof data.vapid_public_key === 'string' ? data.vapid_public_key.trim() : '';
  return k || null;
}

/** Subscribe authed user for workflow push (session + optional X-Session-Token via caller headers). */
export async function subscribeAuthenticatedPush(
  registration: ServiceWorkerRegistration,
  vapidPublicKey: string,
  authHeaders: Record<string, string>
): Promise<boolean> {
  const key = urlBase64ToUint8Array(vapidPublicKey);
  const sub = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key as BufferSource,
  });
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    return false;
  }
  const csrf = getCsrfToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...authHeaders,
  };
  if (csrf) headers['X-CSRFToken'] = csrf;
  const res = await fetch(`${API_BASE}/api/v2/push/subscribe/`, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify({
      subscription: {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      },
    }),
  });
  return res.ok;
}

export async function subscribeHelpQueryPush(
  registration: ServiceWorkerRegistration,
  vapidPublicKey: string,
  submissionId: number,
  token: string
): Promise<boolean> {
  const key = urlBase64ToUint8Array(vapidPublicKey);
  const sub = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key as BufferSource,
  });
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    return false;
  }
  const res = await fetch(`${API_BASE}/api/v2/push/subscribe-anonymous/`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      submission_id: submissionId,
      token,
      subscription: {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      },
    }),
  });
  return res.ok;
}
